import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, UserPlus, Trash2, MoreVertical, Crown,
  Shield, Eye, LogOut, Loader2, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useTeamStore, useAuthStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const TeamsPage = () => {
  const { teams, fetchTeams, createTeam, inviteMember, removeMember, currentTeam, setCurrentTeam } = useTeamStore();
  const { user } = useAuthStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [inviteData, setInviteData] = useState({ email: '', role: 'member' });

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchTeams();
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error(language === 'hi' ? 'टीम का नाम आवश्यक है' : 'Team name is required');
      return;
    }

    setIsCreating(true);
    try {
      const team = await createTeam(newTeam.name, newTeam.description);
      setCurrentTeam(team);
      toast.success(language === 'hi' ? 'टीम बनाई गई' : 'Team created');
      setShowCreateDialog(false);
      setNewTeam({ name: '', description: '' });
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteData.email.trim() || !currentTeam) {
      toast.error(language === 'hi' ? 'ईमेल आवश्यक है' : 'Email is required');
      return;
    }

    setIsInviting(true);
    try {
      await inviteMember(currentTeam.id, inviteData.email, inviteData.role);
      toast.success(language === 'hi' ? 'सदस्य आमंत्रित' : 'Member invited');
      setShowInviteDialog(false);
      setInviteData({ email: '', role: 'member' });
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!currentTeam) return;
    
    try {
      await removeMember(currentTeam.id, memberId);
      toast.success(language === 'hi' ? 'सदस्य हटाया गया' : 'Member removed');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
      case 'owner':
        return <Crown className="h-4 w-4 text-chart-4" />;
      case 'member':
        return <Shield className="h-4 w-4 text-primary" />;
      case 'viewer':
        return <Eye className="h-4 w-4 text-muted-foreground" />;
      default:
        return null;
    }
  };

  const selectedTeam = currentTeam || teams[0];
  const isAdmin = selectedTeam?.members?.some(
    m => m.user_id === user?.id && ['admin', 'owner'].includes(m.role)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            {t('teams')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'hi' ? 'अपनी टीमों को प्रबंधित करें और सहयोग करें' : 'Manage your teams and collaborate'}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="glow-primary"
          data-testid="create-team-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('new_team')}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Teams List */}
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider">
            {language === 'hi' ? 'आपकी टीमें' : 'Your Teams'}
          </h2>
          {teams.length === 0 ? (
            <Card className="glass">
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('no_teams')}</p>
              </CardContent>
            </Card>
          ) : (
            teams.map((team) => (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  className={`glass cursor-pointer card-hover ${
                    selectedTeam?.id === team.id ? 'border-primary/50 bg-primary/10' : ''
                  }`}
                  onClick={() => setCurrentTeam(team)}
                  data-testid={`team-card-${team.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{team.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {team.members?.length || 0} {t('members').toLowerCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </div>

        {/* Team Details */}
        <div className="lg:col-span-2">
          {selectedTeam ? (
            <Card className="glass">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">{selectedTeam.name}</CardTitle>
                  {selectedTeam.description && (
                    <p className="text-sm text-muted-foreground mt-1">{selectedTeam.description}</p>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    onClick={() => setShowInviteDialog(true)}
                    variant="secondary"
                    data-testid="invite-member-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('invite_member')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <h3 className="font-medium mb-4">{t('members')}</h3>
                <div className="space-y-3">
                  {selectedTeam.members?.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-3 rounded-xl bg-secondary/30"
                      data-testid={`member-${member.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {member.name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{member.name}</p>
                            {getRoleIcon(member.role)}
                          </div>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      {isAdmin && member.user_id !== user?.id && member.role !== 'owner' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t('remove_member')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {member.user_id === user?.id && member.role !== 'owner' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          {t('leave_team')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="glass">
              <CardContent className="py-16 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'hi' ? 'टीम का विवरण देखने के लिए एक टीम चुनें' : 'Select a team to view details'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Team Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('new_team')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('team_name')}</Label>
              <Input
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                placeholder={language === 'hi' ? 'टीम का नाम' : 'Team name'}
                className="bg-secondary/50 border-white/10"
                data-testid="team-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('team_description')}</Label>
              <Textarea
                value={newTeam.description}
                onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                placeholder={language === 'hi' ? 'विवरण (वैकल्पिक)' : 'Description (optional)'}
                className="bg-secondary/50 border-white/10"
                data-testid="team-desc-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleCreateTeam}
              disabled={isCreating}
              className="glow-primary"
              data-testid="submit-team-btn"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('create_team')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('invite_member')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="user@example.com"
                  type="email"
                  className="pl-10 bg-secondary/50 border-white/10"
                  data-testid="invite-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'hi' ? 'भूमिका' : 'Role'}</Label>
              <Select
                value={inviteData.role}
                onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
              >
                <SelectTrigger className="bg-secondary/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-chart-4" />
                      {t('admin')}
                    </div>
                  </SelectItem>
                  <SelectItem value="member">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      {t('member')}
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      {t('viewer')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInviteDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleInviteMember}
              disabled={isInviting}
              className="glow-primary"
              data-testid="submit-invite-btn"
            >
              {isInviting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              {t('invite_member')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
